import { FC, useState } from "react";
import { Flex, Text, Box, Select, TextField } from "@radix-ui/themes";

interface TimePickerProps {
  onChange: (timeMs: number) => void;
  initialTime?: { minutes: number; seconds: number; milliseconds: number };
}

/**
 * A `TimePicker` component that allows users to input time values in minutes, seconds, and milliseconds.
 * The component calculates the total time in milliseconds and triggers the `onChange` callback with the computed value.
 *
 * @param {Object} props - The props for the `TimePicker` component.
 * @param {(totalMs: number) => void} props.onChange - Callback function triggered when the time values change.
 * Receives the total time in milliseconds as an argument.
 * @param {Object} [props.initialTime] - The initial time values for the component.
 * @param {string | number} [props.initialTime.minutes=""] - Initial value for minutes. Defaults to an empty string.
 * @param {string | number} [props.initialTime.seconds=""] - Initial value for seconds. Defaults to an empty string.
 * @param {string | number} [props.initialTime.milliseconds=""] - Initial value for milliseconds. Defaults to an empty string.
 *
 * @returns {JSX.Element} A JSX element representing the time picker.
 *
 * @example
 * ```tsx
 * const handleTimeChange = (totalMs: number) => {
 *   console.log(`Total time in milliseconds: ${totalMs}`);
 * };
 *
 * <TimePicker
 *   onChange={handleTimeChange}
 *   initialTime={{ minutes: "1", seconds: "30", milliseconds: "500" }}
 * />;
 * ```
 */

export const TimePicker: FC<TimePickerProps> = ({
  onChange,
  initialTime = { minutes: "", seconds: "", milliseconds: "" },
}) => {
  const [minutes, setMinutes] = useState<string>(
    initialTime.minutes.toString()
  );
  const [seconds, setSeconds] = useState<string>(
    initialTime.seconds.toString()
  );
  const [milliseconds, setMilliseconds] = useState<string>(
    initialTime.milliseconds.toString()
  );

  const updateTime = (
    newMinutes: string,
    newSeconds: string,
    newMilliseconds: string
  ) => {
    setMinutes(newMinutes);
    setSeconds(newSeconds);
    setMilliseconds(newMilliseconds);

    // Calculate total time in milliseconds, handling empty strings
    const minVal = newMinutes === "" ? 0 : parseInt(newMinutes, 10);
    const secVal = newSeconds === "" ? 0 : parseInt(newSeconds, 10);
    const msVal = newMilliseconds === "" ? 0 : parseInt(newMilliseconds, 10);

    const totalMs = minVal * 60 * 1000 + secVal * 1000 + msVal;
    onChange(totalMs);
  };

  return (
    <Flex align="center" gap="1">
      {/* Minutes */}
      <Flex direction="column" gap="1">
        <TextField.Root
          size="2"
          value={minutes}
          onChange={(e) => {
            const value = e.target.value;
            // Allow empty string or up to 2 digits
            if (value === "" || /^\d{1,2}$/.test(value)) {
              updateTime(value, seconds, milliseconds);
            }
          }}
          type="text"
          placeholder="00"
          pattern="[0-9]*"
          inputMode="numeric"
        />
        <Text as="label" size="1" color="gray">
          Minutes
        </Text>
      </Flex>

      {/* Seconds */}
      <Flex direction="column" gap="1">
        <TextField.Root
          size="2"
          value={seconds}
          onChange={(e) => {
            const value = e.target.value;
            // Allow empty string or up to 2 digits
            if (value === "" || /^\d{1,2}$/.test(value)) {
              updateTime(minutes, value, milliseconds);
            }
          }}
          type="text"
          placeholder="00"
          pattern="[0-9]*"
          inputMode="numeric"
        />
        <Text as="label" size="1" color="gray">
          Seconds
        </Text>
      </Flex>

      {/* Milliseconds */}
      <Flex direction="column" gap="1">
        <TextField.Root
          size="2"
          value={milliseconds}
          onChange={(e) => {
            const value = e.target.value;
            // Allow empty string or up to 3 digits
            if (value === "" || /^\d{1,3}$/.test(value)) {
              updateTime(minutes, seconds, value);
            }
          }}
          type="text"
          placeholder="000"
          pattern="[0-9]*"
          inputMode="numeric"
        />
        <Text as="label" size="1" color="gray">
          Milliseconds
        </Text>
      </Flex>
    </Flex>
  );
};
